import "./ResultPanel.css";
import MultiTabResult from "@/layouts/results/MultiTabResult";
import type { ToolSSEvent } from "@/lib/queries/tools/useToolSubmissionSSE";
import { Spinner } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { processHtmlContent } from "@/lib/utils/processHtmlContent";
import { ToolResponse } from "@/lib/types/ToolModel";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import DocumentStatuses, {
  type DocumentStatusItem,
} from "@/components/results/DocumentStatuses";
import { Progress } from "@/components/ui/progress";

function ResultsLoader({
  isLoading,
  appResult,
}: {
  isLoading: boolean;
  appResult: string;
}) {
  const { t } = useTranslation("common");

  if (isLoading) {
    const loaderConfig: Record<string, { message: string; time: number }> = {
      "communication-generator": {
        message: t("loading.communicationGenerator.generating"),
        time: 5000,
      },
      professor: {
        message: t("loading.researcher.investigating"),
        time: 15000,
      },
      "lesson-lab": {
        message: t("loading.lessonLab.building"),
        time: 35000,
      },
      "resource-customiser": {
        message: t("loading.resourceCustomiser.customising"),
        time: 60000,
      },
      "catchup-coach": {
        message: t("loading.catchupCoach.coaching"),
        time: 60000,
      },
    };

    const cfg = loaderConfig[appResult];
    if (!cfg) return <div>{t("results.noRegisteredPane")}</div>;
    return (
      <div className="flex flex-col items-center justify-center h-full text-2xl">
        <Spinner className="w-12 h-12 animate-spin text-dark-grey" />
        <h1 className="text-center text-main-title max-w-96 mt-9">
          {cfg.message}
        </h1>
      </div>
    );
  }

  switch (appResult) {
    case "communication-generator":
      return (
        <section className="flex flex-col items-center justify-center h-full text-2xl">
          <h1 className="font-semibold text-center max-w-96">
            {t("results.placeholder.default")}
          </h1>
        </section>
      );
    default:
      return (
        <section className="flex flex-col items-center justify-center h-full text-2xl">
          <h1 className="font-semibold text-center max-w-96">
            {t("results.generating")}
          </h1>
        </section>
      );
  }
}

function ResultPanel({
  result,
  appResult,
  isLoading,
  toolResponse,
  sseEvents,
  sseError,
  loadingStatuses,
  loadingStage,
  loadingProgress,
  onCancel,
}: {
  result:
    | { language: string; body: string; format: string; type: string }[]
    | undefined;
  appResult: string;
  isLoading: boolean;
  toolResponse?: ToolResponse | null;
  sseEvents?: ToolSSEvent[];
  sseError?: Error | null;
  loadingStatuses?: DocumentStatusItem[];
  loadingStage?: string | null;
  loadingProgress?: {
    step: number;
    totalsteps: number;
    percent: number;
  } | null;
  onCancel?: () => void;
}) {
  const { t } = useTranslation("common");
  const [errorDismissed, setErrorDismissed] = useState(false);
  useEffect(() => {
    setErrorDismissed(false);
  }, [sseError]);

  const partialResults = useMemo(() => {
    const map = new Map<
      string,
      {
        language: string;
        body: string;
        type: string;
        format: string;
        style?: string;
        trackingIds?: string[];
      }
    >();
    (sseEvents || []).forEach((ev) => {
      if (ev.type === "output") {
        const res: any = (ev as any)?.body?.result;
        if (!res || typeof res.body !== "string") return;
        const processed = processHtmlContent(res);
        const type = processed.type || res.type;
        const language = processed.language || res.language;
        if (!type || !language) return;
        const key = `${type}__${(language || "").toLowerCase()}`;
        map.set(key, {
          language,
          body: processed.body,
          type,
          format: processed.format || res.format,
          style: processed.style,
          trackingIds: processed.trackingIds,
        });
      }
      if (ev.type === "complete") {
        const results: any[] = ((ev as any)?.body?.results || []).filter(
          (r: any) => r && typeof r.body === "string"
        );
        results.forEach((r) => {
          const processed = processHtmlContent(r);
          const type = processed.type || r.type;
          const language = processed.language || r.language;
          if (!type || !language) return;
          const key = `${type}__${(language || "").toLowerCase()}`;
          map.set(key, {
            language,
            body: processed.body,
            type,
            format: processed.format || r.format,
            style: processed.style,
            trackingIds: processed.trackingIds,
          });
        });
      }
    });
    return Array.from(map.values());
  }, [sseEvents]);

  const [resetCounter, setResetCounter] = useState(0);
  useEffect(() => {
    if (loadingStage === "done") {
      setResetCounter((c) => c + 1);
    }
  }, [loadingStage]);

  const resultsToDisplay = useMemo(() => {
    const map = new Map<
      string,
      {
        language: string;
        body: string;
        type: string;
        format: string;
        style?: string;
        trackingIds?: string[];
      }
    >();
    (result || []).forEach((r) => {
      const key = `${r.type}__${(r.language || "").toLowerCase()}`;
      map.set(key, r);
    });
    partialResults.forEach((r) => {
      const key = `${r.type}__${(r.language || "").toLowerCase()}`;
      map.set(key, r);
    });
    return Array.from(map.values());
  }, [result, partialResults]);

  let mainContent: JSX.Element | null = null;
  if (!isLoading && resultsToDisplay.length === 0) {
    mainContent = (
      <section className="flex flex-col items-center justify-center h-full text-2xl">
        <h1 className="font-semibold text-center max-w-96">
          {t("results.placeholder.withPeriod")}
        </h1>
      </section>
    );
  }

  if (resultsToDisplay.length > 0) {
    switch (appResult) {
      case "professor":
        mainContent = (
          <div className="h-full">
            <MultiTabResult
              results={resultsToDisplay}
              references={toolResponse?.references}
              resetSignal={resetCounter}
            />
          </div>
        );
        break;
      case "lesson-lab":
        mainContent = (
          <div className="h-full">
            <MultiTabResult
              results={resultsToDisplay}
              references={toolResponse?.references}
              resetSignal={resetCounter}
            />
          </div>
        );
        break;
      case "communication-generator":
        mainContent = (
          <div className="h-full">
            <MultiTabResult
              results={resultsToDisplay}
              references={toolResponse?.references}
              resetSignal={resetCounter}
            />
          </div>
        );
        break;
      case "resource-customiser":
        mainContent = (
          <div className="h-full">
            <MultiTabResult
              results={resultsToDisplay}
              references={toolResponse?.references}
              resetSignal={resetCounter}
            />
          </div>
        );
        break;
      case "catchup-coach":
        mainContent = (
          <div className="h-full">
            <MultiTabResult
              results={resultsToDisplay}
              references={toolResponse?.references}
              resetSignal={resetCounter}
            />
          </div>
        );
        break;
      default:
        mainContent = <div>{t("results.noRegisteredPane")}</div>;
        break;
    }
  } else if (isLoading) {
    // If desktop-provided statuses are available, show them instead of generic loader
    if (loadingStatuses && loadingStatuses.length > 0) {
      const percentRaw = loadingProgress
        ? typeof loadingProgress.percent === "number"
          ? loadingProgress.percent
          : loadingProgress.totalsteps
            ? (loadingProgress.step / loadingProgress.totalsteps) * 100
            : 0
        : 0;
      const percent = Math.max(0, Math.min(100, Math.round(percentRaw)));
      mainContent = (
        <section className="flex flex-col items-center justify-center h-full gap-6">
          {loadingStage !== "done" && (
            <div className="w-full max-w-xl">
              <Progress
                value={percent}
                aria-label={t("generation.progress", {
                  defaultValue: "Progress",
                })}
              />

              <div className="flex flex-row items-center justify-center gap-2 mt-2 text-sm text-foreground/70">
                <Spinner className="w-8 h-8 animate-spin text-dark-grey" />
                {t("generation.percentComplete", {
                  defaultValue: "{{percent}}% complete",
                  percent,
                })}
              </div>
            </div>
          )}
          <div className="w-full max-w-xl">
            <DocumentStatuses statuses={loadingStatuses} stage={loadingStage} />
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1 text-sm font-medium text-red-700 border border-red-200 rounded bg-red-50 hover:bg-red-100"
            >
              {t("common:cancel", { defaultValue: "Cancel" })}
            </button>
          )}
        </section>
      );
    } else {
      mainContent = <ResultsLoader isLoading={true} appResult={appResult} />;
    }
  }
  return (
    <div className="relative h-full">
      {mainContent}
      {!!sseError && !errorDismissed && (
        <div
          className={cn(
            "fixed left-1/2 -translate-x-1/2 bottom-4 z-50 w-[min(92vw,48rem)]",
            "transition-all duration-200"
          )}
        >
          <Alert variant="destructive" className="relative pr-10">
            <AlertTitle>
              {t("errors.generationFailed", {
                defaultValue: "Generation failed",
              })}
            </AlertTitle>
            <AlertDescription>
              {t("errors.tryAgain", {
                defaultValue: "Please try again or cancel and resubmit.",
              })}
            </AlertDescription>
            <button
              type="button"
              className="absolute top-2 right-2 text-foreground/70 hover:text-foreground"
              aria-label={t("common:close", { defaultValue: "Close" })}
              onClick={() => setErrorDismissed(true)}
            >
              <X className="w-5 h-5" />
            </button>
          </Alert>
        </div>
      )}
    </div>
  );
}

export default ResultPanel;
